import React from "react";
// import { ArrowDownIcon, ArrowUpIcon } from "@heroicons/react/20/solid";
import {
  ChartBarIcon,
  CheckCircleIcon,
  ClockIcon,
  ScaleIcon,
} from "@heroicons/react/24/outline";
import { Skeleton } from "primereact/skeleton";

import "../../views/dashboard/dashboard.css";

export type OperationalKpiIcon =
  | "inProgress"
  | "completed"
  | "manufacturedWeight"
  | "targetWeight";

const PLACEHOLDER_STATS: OperationalStat[] = [
  { name: "—", icon: "inProgress" },
  { name: "—", icon: "completed" },
  { name: "—", icon: "manufacturedWeight" },
  { name: "—", icon: "targetWeight" },
];

const KPI_ICONS: Record<
  OperationalKpiIcon,
  React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
  inProgress: ClockIcon,
  completed: CheckCircleIcon,
  manufacturedWeight: ScaleIcon,
  targetWeight: ChartBarIcon,
};

interface OperationalStat {
  name: string;
  stat?: string;
  subtitle?: string | null;
  // change?: string;
  // changeType?: string;
  icon?: OperationalKpiIcon;
}

const KPI_ACCENTS = [
  "bg-emerald-50 text-emerald-600",
  "bg-green-50 text-green-600",
  "bg-blue-50 text-blue-600",
  "bg-orange-50 text-orange-600",
];

interface OperationalAnalyticsStripProps {
  stats?: OperationalStat[];
  loading?: boolean;
}

function KpiValueSkeleton() {
  return (
    <Skeleton
      width="4rem"
      height="1.25rem"
      borderRadius="8px"
      className="block max-w-full"
    />
  );
}

function KpiIcon({ iconKey }: { iconKey: OperationalKpiIcon }) {
  const Icon = KPI_ICONS[iconKey];
  return <Icon className="h-5 w-5" strokeWidth={2} aria-hidden />;
}

export default function OperationalAnalyticsStrip({
  stats = [],
  loading = false,
}: OperationalAnalyticsStripProps) {
  if (!loading && (!stats || stats.length === 0)) {
    return null;
  }

  const displayStats =
    loading && (!stats || stats.length === 0) ? PLACEHOLDER_STATS : stats;

  return (
    <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {displayStats.map((item, index) => {
        const accent = KPI_ACCENTS[index % KPI_ACCENTS.length];
        const iconKey =
          item.icon ??
          PLACEHOLDER_STATS[index % PLACEHOLDER_STATS.length].icon ??
          "inProgress";
        // const showChange =
        //   !loading && item.change && item.changeType && item.change !== "0%";

        return (
          <div
            key={`${item.name}-${index}`}
            className="compact-kpi-card text-left !cursor-default !p-3 hover:!translate-y-0 sm:!p-3.5"
            aria-busy={loading}
          >
            <div className="flex items-center gap-3">
              <span className={`compact-kpi-icon ${accent}`}>
                <KpiIcon iconKey={iconKey} />
              </span>
              <div className="flex min-w-0 flex-1 flex-col justify-center">
                <p className="text-sm font-bold leading-snug text-[#0F172A] sm:text-base">
                  {item.name}
                </p>
                {item.subtitle ? (
                  <p className="mt-0.5 truncate text-xs leading-snug text-[#64748B] sm:text-sm">
                    {item.subtitle}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="mt-3 flex items-end justify-between gap-3">
              <p className="min-w-0 text-lg font-semibold leading-tight tracking-tight text-[#0F172A] tabular-nums sm:text-xl">
                {loading ? <KpiValueSkeleton /> : (item.stat ?? "—")}
              </p>
              {/* Previous-period comparison (disabled for now)
              {showChange ? (
                <span
                  className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                    item.changeType === "increase"
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {item.changeType === "increase" ? (
                    <ArrowUpIcon className="mr-0.5 h-3.5 w-3.5" aria-hidden />
                  ) : (
                    <ArrowDownIcon className="mr-0.5 h-3.5 w-3.5" aria-hidden />
                  )}
                  {item.change}
                </span>
              ) : null}
              */}
            </div>
          </div>
        );
      })}
    </section>
  );
}
